import axios from "axios";
import { baseURL } from "../../config/variables";
import { generateRoleMessage } from "./roleMessages";
import { ShowAlertProps } from "../../Components/common/types";
import { errLog, log, warnLog } from "../logger";
import { getErrorMessage } from "../errors";

// Create an axios instance
const api = axios.create({ 
  baseURL,
  withCredentials: true, // Include cookies in requests
});

let interceptorsSet = false; // Flag to track if interceptors are already set

export const setupInterceptors = (
  refreshAccessToken: () => Promise<string | null>,
  tokenRef: React.MutableRefObject<string | null>,
  showAlert: ShowAlertProps["showAlert"]
) => {
  if (interceptorsSet) return; // Prevent setting interceptors multiple times
  interceptorsSet = true;

  log("Token ref in api interceptor", tokenRef)

  //Add a request interceptor
  api.interceptors.request.use(
    (config) => {
      // Add the access token to the request headers
      if (tokenRef.current) {
        console.log("processing...");
        config.headers.Authorization = `Bearer ${tokenRef.current}`;
        log("✅ Attached token:", tokenRef.current); // Debug
      } else {
        warnLog("❌ No token to attach. N/B Fetch projects is exceptional");
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // //mock a 401 error
  // api.interceptors.request.use(
  //   (config) => {
  //     console.log("Mocking missing token...");
  //     return Promise.reject({ response: { status: 401 } });
  //   },
  //   (error) => Promise.reject(error)
  // );

  // Add a response interceptor
  api.interceptors.response.use(
    (response) => response, //normal response from an endpoint
    async (error) => {
      // if it fails, check missing token error, JWT verify errors and verify role errors from backend
      const originalRequest = error.config;

      //Access status for both veirify jwt and roles
      const jwtAndRoleStatus = error?.response?.data?.status;
      const expired = jwtAndRoleStatus === "expired";
      const invalid = jwtAndRoleStatus === "invalid";
      const failedVerification = jwtAndRoleStatus === "failed verification";
      const missingRole = jwtAndRoleStatus === "missing role";
      const checkJWTStatus = expired || invalid || failedVerification;
      const checkRoleStatus = missingRole;

      // Check if the error is due to missing access token or roles (status 401) (includes, missing auth header, token missing in Bearer or role missing in request)
      if (error.response?.status === 401) {
        await refreshAccessToken(); //This helps to invalidate and clear the expired refresh token from backend, and initiates a front end logout
        return Promise.reject(error);
      }

      // Handle verify JWT errors can be caused by expired, invalid orverification of access token
      if (error.response?.status === 403 && checkJWTStatus  && !originalRequest._retry) {
        originalRequest._retry = true; // Mark the request as retried

        try {
          // Attempt to refresh the access token
          log("Retrying refresh fn");
          const newAccessToken = await refreshAccessToken();

          if (newAccessToken) {
            tokenRef.current = newAccessToken; // Update ref
            originalRequest.headers.Authorization = `Bearer ${tokenRef.current}`; // Update the request headers with the new access token

            return api(originalRequest); // Retry the original request with the new access token
          }
        } catch (refreshError) {
          errLog("Token refresh failed:", getErrorMessage(refreshError));
          // Do NOT call logout again since `refreshAccessToken` already does this
          return Promise.reject(refreshError);
        }
      }

      //Check verify role errors
      if (error.response?.status === 403 && checkRoleStatus) {
        const { userRoles, requiredRoles } = error.response.data;
        const errorMessage = generateRoleMessage(
          requiredRoles,
          "add a project",
          userRoles
        );
        showAlert(true, "Access Denied", errorMessage, false);

        return Promise.reject(error);
      }

      // Handle other errors
      return Promise.reject(error);
    }
  );
};

export default api;
